import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

function Point1({ projectId }) {
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableAnalyses, setAvailableAnalyses] = useState({});

  useEffect(() => {
    checkAvailableAnalyses();
    loadExecutiveSummary();
  }, [projectId]);

  const checkAvailableAnalyses = () => {
    const analyses = {
      point2: localStorage.getItem(`analysis_point2_${projectId}`) || localStorage.getItem(`analysis_docs_${projectId}`) || localStorage.getItem(`analysis_web_${projectId}`),
      point3: localStorage.getItem(`analysis_point3_${projectId}`) || localStorage.getItem(`analysis_internal_${projectId}`) || localStorage.getItem(`analysis_external_${projectId}`) || localStorage.getItem(`analysis_dafo_${projectId}`),
      point4: localStorage.getItem(`analysis_point4_${projectId}`) || localStorage.getItem(`market_criteria_${projectId}`) || localStorage.getItem(`final_recommendations_${projectId}`),
      //point5: localStorage.getItem(`analysis_point5_${projectId}`) || localStorage.getItem(`access_strategies_${projectId}`)
    };

    const availableCount = Object.values(analyses).filter(Boolean).length;
    setAvailableAnalyses({ ...analyses, count: availableCount });
  };

  const loadExecutiveSummary = () => {
    const saved = localStorage.getItem(`executive_summary_${projectId}`);
    if (saved) {
      setExecutiveSummary(JSON.parse(saved));
    }
  };

  const generateExecutiveSummary = async () => {
    setLoading(true);
    try {
      const analysisContent = [];
      const maxSectionSize = 4000; // Límite por sección para mantener el prompt bajo control
      
      if (availableAnalyses.point2) {
        const point2Data = JSON.parse(availableAnalyses.point2);
        const truncatedContent = point2Data.content.substring(0, maxSectionSize);
        analysisContent.push(`PRESENTACIÓN DE LA EMPRESA:\n${truncatedContent}`);
      }
      
      if (availableAnalyses.point3) {
        const point3Data = JSON.parse(availableAnalyses.point3);
        const truncatedContent = point3Data.content.substring(0, maxSectionSize);
        analysisContent.push(`ANÁLISIS DE SITUACIÓN:\n${truncatedContent}`);
      }
      
      if (availableAnalyses.point4) {
        const point4Data = JSON.parse(availableAnalyses.point4);
        const truncatedContent = point4Data.content.substring(0, maxSectionSize);
        analysisContent.push(`SELECCIÓN DE MERCADOS:\n${truncatedContent}`);
      }
      
      if (availableAnalyses.point5) {
        const point5Data = JSON.parse(availableAnalyses.point5);
        const truncatedContent = point5Data.content.substring(0, maxSectionSize);
        analysisContent.push(`PLAN DE ACCESO:\n${truncatedContent}`);
      }

      let combinedContent = analysisContent.join('\n\n---\n\n');
      
      // Verificación adicional del tamaño total
      if (combinedContent.length > 20000) {
        console.log('Contenido muy grande para resumen ejecutivo, aplicando truncamiento adicional');
        combinedContent = combinedContent.substring(0, 20000) + '\n\n[Contenido truncado para optimización...]';
      }
      
      console.log('Tamaño del contenido para resumen ejecutivo:', combinedContent.length);

      const executivePrompt = `Genera un Resumen Ejecutivo profesional basándote en los siguientes análisis completados:

${combinedContent}

ESTRUCTURA DEL RESUMEN EJECUTIVO:

1. EMPRESA Y CONTEXTO (2-3 párrafos)
   - Presentación breve de la empresa
   - Situación actual y motivación para internacionalización

2. OPORTUNIDAD DE NEGOCIO (2-3 párrafos)
   - Mercados objetivo identificados
   - Potencial de crecimiento y oportunidades

3. ESTRATEGIA RECOMENDADA (2-3 párrafos)
   - Modalidades de entrada propuestas
   - Ventajas competitivas a explotar

4. INVERSIÓN Y RETORNO (1-2 párrafos)
   - Inversión requerida estimada
   - ROI esperado y timeline

5. FACTORES CRÍTICOS Y PRÓXIMOS PASOS (1-2 párrafos)
   - Riesgos principales y mitigación
   - Acciones inmediatas recomendadas

REQUISITOS:
- Máximo 800 palabras
- Lenguaje ejecutivo, directo y profesional
- Enfoque en decisiones y números clave
- Conclusiones accionables

Genera un resumen que permita a la dirección tomar una decisión informada sobre la internacionalización.`;

      const response = await axios.post('/api/analysis/', {
        project_id: projectId,
        point_number: 1,
        prompt: executivePrompt,
        use_web: false,
        search_terms: '',
        web_only: false
      });

      const summaryData = {
        content: response.data.content,
        sources: response.data.sources,
        generatedAt: new Date().toISOString(),
        basedOn: Object.keys(availableAnalyses).filter(key => key !== 'count' && availableAnalyses[key])
      };

      setExecutiveSummary(summaryData);
      localStorage.setItem(`executive_summary_${projectId}`, JSON.stringify(summaryData));

    } catch (error) {
      console.error('Error generating executive summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearExecutiveSummary = () => {
    setExecutiveSummary(null);
    localStorage.removeItem(`executive_summary_${projectId}`);
  };

  if (availableAnalyses.count === 0) {
    return (
      <div className="point-content">
        <h2>1. Resumen Ejecutivo</h2>
        <div className="info-box">
          Este punto se generará automáticamente al completar el resto del análisis.
        </div>
        <div className="summary-status">
          <p>Para generar el resumen ejecutivo, completa al menos uno de los siguientes puntos:</p>
          <ul>
            <li>2. Presentación de la Empresa</li>
            <li>3. Análisis de Situación</li>
            <li>4. Selección de Mercados</li>
            {/* <li>5. Plan de Acceso</li> */}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="point-content">
      <h2>1. Resumen Ejecutivo</h2>
      
      <div className="summary-controls">
        <div className="available-analyses">
          <h3>Análisis disponibles para el resumen:</h3>
          <div className="analyses-grid">
            <div className={`analysis-item ${availableAnalyses.point2 ? 'completed' : 'pending'}`}>
              <span>2. Presentación de la Empresa</span>
              <span className="status">{availableAnalyses.point2 ? '✓' : '○'}</span>
            </div>
            <div className={`analysis-item ${availableAnalyses.point3 ? 'completed' : 'pending'}`}>
              <span>3. Análisis de Situación</span>
              <span className="status">{availableAnalyses.point3 ? '✓' : '○'}</span>
            </div>
            <div className={`analysis-item ${availableAnalyses.point4 ? 'completed' : 'pending'}`}>
              <span>4. Selección de Mercados</span>
              <span className="status">{availableAnalyses.point4 ? '✓' : '○'}</span>
            </div>
            {/*<div className={`analysis-item ${availableAnalyses.point5 ? 'completed' : 'pending'}`}>
              <span>5. Plan de Acceso</span>
              <span className="status">{availableAnalyses.point5 ? '✓' : '○'}</span>
            </div>*/}
          </div>
        </div>

        <div className="summary-actions">
          {!executiveSummary && (
            <button 
              onClick={generateExecutiveSummary}
              disabled={loading}
              className="generate-btn"
            >
              {loading ? 'Generando Resumen...' : 'Generar Resumen Ejecutivo'}
            </button>
          )}
          
          {executiveSummary && (
            <div className="action-buttons">
              <button 
                onClick={generateExecutiveSummary}
                disabled={loading}
                className="regenerate-btn"
              >
                {loading ? 'Regenerando...' : 'Regenerar Resumen'}
              </button>
              <button 
                onClick={clearExecutiveSummary}
                className="clear-btn"
              >
                Limpiar Resumen
              </button>
            </div>
          )}
        </div>
      </div>

      {executiveSummary && (
        <div className="executive-summary-result">
          <div className="summary-header">
            <h3>Resumen Ejecutivo Generado</h3>
            <div className="summary-meta">
              <span>Generado: {new Date(executiveSummary.generatedAt).toLocaleString()}</span>
              <span>Basado en: {executiveSummary.basedOn.length} análisis completados</span>
            </div>
          </div>
          
          <div className="summary-content">
            <ReactMarkdown>{executiveSummary.content}</ReactMarkdown>
          </div>

          {executiveSummary.sources && executiveSummary.sources.length > 0 && (
            <div className="sources">
              <h4>Fuentes utilizadas:</h4>
              <ul>
                {executiveSummary.sources.map((source, index) => (
                  <li key={index}>{source}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Point1;